import { Add } from '@mui/icons-material';
import DealComponent from '../deal/deal_component';
import DeleteStageModal from './modal/delete_stage';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import React from 'react';
import { selectStageForBulkEdit } from '../../redux/slices/pipeline';
import { settingSelectedStage } from '../../redux/slices/stage';
import { StageObject } from '../../params/pipeline';
import { styled } from '@mui/material/styles';
import { yellow } from '@mui/material/colors';
import { AppDispatch, RootState } from '../../redux/store';
import { Box, Button, Checkbox, IconButton, Menu, MenuItem, Paper, Typography } from '@mui/material';
import { brand, gray } from '../../theme/themePrimitives';
import { Draggable, Droppable } from 'react-beautiful-dnd';
import { useDispatch, useSelector } from 'react-redux';

/**
 * @heading1: StageComponent
 * @paragraph: This component represents a stage in a pipeline, including its deals and actions like editing, deleting, or adding a deal.
 * It supports drag-and-drop functionality for reordering stages and deals.
 */
const StepColumn = styled(Paper)(({ theme }) => ({
    flex: 1,
    padding: theme.spacing(2),
    minWidth: '300px',
    border: '1px solid #f4f4f4',
    boxShadow: 'none',
    borderRadius: '8px',
    marginBottom: '10px',
    maxWidth: '300px'
}));

/**
 * @interface: StageProps
 * @property: {StageObject} stage - The stage object containing details about the stage.
 * @property: {number} index - The index of the stage in the pipeline.
 * @property: {() => void} onAddDeal - Callback function to handle adding a new deal to the stage.
 * @property: {() => void} onUpdateCall - Callback function to handle updating the stage.
 */
interface StageProps {
    stage: StageObject;
    index: number;
    onAddDeal: () => void;
    onUpdateCall: () => void;
}

/**
 * @heading2: StageComponent
 * @param: {StageProps} props - The props required for this component.
 * @returns: {JSX.Element} - The rendered SelectPipeline component.
 */
const StageComponent: React.FC<StageProps> = ({ stage, index, onAddDeal, onUpdateCall }) => {
    /**
     * @heading3: Variables
     * @ol:
     * disaptch: {AppDisptach} - The Redux dispatch function to trigger actions.
     * anchorEl/isDeleting: React state for modal and popup menu.
     */
    const dispatch = useDispatch<AppDispatch>();
    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
    const { bulkEditMode, selectedStages, loaders, selectedPipeline } = useSelector((state: RootState) => state.pipeline);
    const [isDeleting, setIsDeleting] = React.useState<boolean>(false);

    /**
     * @heading3: Functions
     * @heading4: handleMenuClick
     * @paragraph: Handles the click event to open the action menu for the stage.
     * @param: {React.MouseEvent<HTMLElement>} event - The click event object.
     */
    const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };

    /**
     * @heading4: handleClose
     * @paragraph: Closes the action menu.
     */
    const handleClose = () => {
        setAnchorEl(null);
    };

    /**
     * @heading4: deleteModalHandler
     * @paragraph: Opens the delete confirmation modal and sets the selected stage for deletion.
     * @param: {number} stageId - The ID of the stage to be deleted.
     */
    const deleteModalHandler = (stageId: number) => {
        dispatch(settingSelectedStage(stageId));
        setIsDeleting(true);
    };

    /**
     * @heading4: hideDeleteModal
     * @paragraph: Closes the delete confirmation modal.
     */
    const hideDeleteModal = () => setIsDeleting(false);

    /**
     * @heading4: updateStageHandler
     * @paragraph: Sets the selected stage for editing and triggers the update callback.
     * @param: {StageObject} stageObj - The stage object to be updated.
     */
    const updateStageHandler = (stageObj: StageObject) => {
        dispatch(settingSelectedStage(stageObj.id));
        onUpdateCall();
    };

    const handleBulkEditStage = (stageId: number) => {
        if (stage.deals.length > 0) {
            dispatch(selectStageForBulkEdit(stageId));
        }
    };

    return (
        <Draggable draggableId={`stage-${stage.id}`} index={index}>
            {(provided) => (
                <Box marginRight="20px" ref={provided.innerRef} {...provided.draggableProps}>
                    {/* Stage container */}
                    <StepColumn sx={{ bgcolor: gray[100] }}>
                        {/* Stage header */}
                        <Box display="flex" justifyContent="space-between" alignItems="center" mb={1} {...provided.dragHandleProps}>
                            <Typography variant="subtitle1" whiteSpace="nowrap" overflow="hidden" textOverflow="ellipsis" fontWeight="600">
                                {stage.stage_name}
                            </Typography>
                            {/* Action menu */}
                            {bulkEditMode
                                ? (
                                    <Checkbox
                                        checked={selectedStages.includes(stage.id)}
                                        onChange={() => handleBulkEditStage(stage.id)}
                                        disabled={loaders.isLoading}
                                    />
                                )
                                : <IconButton size="small" onClick={handleMenuClick}>
                                    <MoreVertIcon />
                                </IconButton>}
                            <Menu
                                anchorEl={anchorEl}
                                open={Boolean(anchorEl)}
                                onClose={handleClose}
                                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                            >
                                <MenuItem onClick={() => updateStageHandler(stage)}>Edit</MenuItem>
                                <MenuItem onClick={() => deleteModalHandler(stage.id)}>Delete</MenuItem>
                            </Menu>
                        </Box>
                        {/* Stage details */}
                        <Typography variant="body2" color="textSecondary">
                            {stage.deals.length} Deal - ${selectedPipeline?.deal_default_value}
                        </Typography>
                        {/* Deal list */}
                        <Droppable droppableId={`stage-${stage.id}`} type="deal">
                            {(providedDeal) => (
                                <Box
                                    ref={providedDeal.innerRef}
                                    {...providedDeal.droppableProps}
                                    sx={{
                                        // backgroundColor: (theme) => (theme.palette.mode === 'dark' ? 'transparent' : snapshot.isDraggingOver ? '#f4f8fb' : '#f4f8fb'),
                                        backgroundColor: 'transparent',
                                        marginTop: '10px',
                                        minHeight: '50px',
                                        borderRadius: '8px'
                                    }}
                                >
                                    {stage.deals.map((deal, dealIndex) => {
                                        if (deal.status === 'pending') {
                                            return (<Box
                                            key={deal.id}
                                                    mb={1}
                                                    p={2}
                                                    sx={{
                                                        'border': '1px solid',
                                                        'borderColor': '#ddd',
                                                        'borderRadius': '8px',
                                                        'backgroundColor': (theme) => (theme.palette.mode === 'dark' ? '#2a2a2a' : 'white'),
                                                        '&:hover': {
                                                            borderWidth: '2px',
                                                            borderColor: brand[300]
                                                        }
                                                    }}
                                                >
                                                    <DealComponent deal={deal} />
                                                </Box>);
                                        }
                                        return <Draggable key={deal.id} draggableId={`deal-${deal.id}`} index={dealIndex}>
                                            {(providedDealItem) => (
                                                <Box
                                                    ref={providedDealItem.innerRef}
                                                    {...providedDealItem.draggableProps}
                                                    {...providedDealItem.dragHandleProps}
                                                    mb={1}
                                                    p={2}
                                                    sx={{
                                                        'border': '1px solid',
                                                        'borderColor': deal.status === 'completed' ? yellow[600] : '#ddd',
                                                        'borderRadius': '8px',
                                                        'backgroundColor': (theme) => (theme.palette.mode === 'dark' ? '#2a2a2a' : 'white'),
                                                        '&:hover': {
                                                            borderWidth: '2px',
                                                            borderColor: brand[300]
                                                        },
                                                        ...providedDealItem.draggableProps.style
                                                    }}
                                                >
                                                    <DealComponent deal={deal} />
                                                </Box>
                                            )}
                                        </Draggable>;
})}
                                    {providedDeal.placeholder}
                                </Box>
                            )}
                        </Droppable>
                    </StepColumn>
                    {/* Add Deal Button */}
                    {!bulkEditMode && <Button variant="outlined" size="small" color="primary" startIcon={<Add />} fullWidth onClick={onAddDeal}>
                        Add a deal
                    </Button>}
                    {/* Delete Stage Modal */}
                    <DeleteStageModal open={isDeleting} onClose={hideDeleteModal} />
                </Box>
            )}
        </Draggable>
    );
};

export default StageComponent;
